// @ts-nocheck
import _ from 'lodash';
import React, { useCallback } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles, Box, Tabs, Tab } from '@material-ui/core';
import {
  useAlert,
  useHistoryState,
  useIfMounted,
  useOnMount,
  useOnPendingActionFinish,
} from '../../../../../common/hooks';
import { AlertSeverity } from '../../../../../common/providers';
import { AppRoute } from '../../../../app';
import { EmptyState, LoadingWrapper, Panel, TabPanel } from '../../../../../common/components';
import { selectPendingActions } from '../../../../../common/state/pending';
import {
  createContract,
  fetchCompany,
  fetchProducts,
  updateCompany,
} from '../../../state/companyActions';
import { selectCompanyDetails, selectCompanyProducts } from '../../../state/companySelectors';
import CompanyDetailsTab from './partials/CompanyDetailsTab';
import CompanyContractsTab from './partials/CompanyContractsTab';
import { FETCH_COMPANY } from '../../../state/companyActionTypes';

const TabIndex = {
  Details: 0,
  Contracts: 1,
};

const useStyles = makeStyles(theme => ({
  root: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
  },
  panel: {
    paddingTop: '68px',
    paddingLeft: '0',
    paddingRight: '0',
    paddingBottom: '0',
  },
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    '& .MuiTabs-indicator': {
      display: 'none',
    },
  },
  fullHeight: {
    flex: '1',
    height: '100%',
  },
}));

function CompanyDetailsPage() {
  const [loading, setLoading] = useHistoryState('loading', true);
  const [tabIndex, setTabIndex] = useHistoryState('tabIndex', TabIndex.Details);
  const [showActiveContracts, setShowActiveContracts] = useHistoryState('activeContracts', true);
  const [t] = useTranslation();
  const { showAlert } = useAlert();
  const { companyId } = useParams();
  const history = useHistory();
  const companyDetails = useSelector(selectCompanyDetails);
  const products = useSelector(selectCompanyProducts);
  const pendingActions = useSelector(selectPendingActions);
  const classes = useStyles();
  const ifMounted = useIfMounted();
  const dispatch = useDispatch();

  useOnMount(() => {
    async function handleFetchData() {
      try {
        if (!companyDetails) {
          setLoading(true);
        }
        await dispatch(fetchCompany(companyId));
      } catch (e) {
        showAlert({
          title: t('company.companyDetailsFetchError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
      }
    }
    handleFetchData();
  });

  useOnPendingActionFinish([FETCH_COMPANY], async () => {
    try {
      await dispatch(fetchProducts(companyDetails.segment));
    } catch (e) {
      showAlert({
        title: t('company.companyDetailsFetchError'),
        message: e.message,
        severity: AlertSeverity.Error,
      });
    } finally {
      ifMounted(setLoading(false));
    }
  });

  const handleUpdateCompany = useCallback(
    async companyData => {
      try {
        await dispatch(updateCompany(companyData));
        showAlert({
          title: t('common.success'),
          message: t('company.companyUpdateSuccess'),
          severity: AlertSeverity.Success,
        });
      } catch (e) {
        showAlert({
          title: t('company.companyUpdateError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
        throw e; // Rethrow to catch later
      }
    },
    [dispatch, showAlert, t]
  );

  const handleCreateContract = useCallback(
    async contractData => {
      try {
        const { id } = await dispatch(createContract(contractData));
        await dispatch(fetchCompany(companyId));
        ifMounted(() =>
          history.push(
            AppRoute.ContractDetails.replace(':companyId', companyId).replace(':contractId', id),
            { from: history.location }
          )
        );
        showAlert({
          title: t('common.success'),
          message: t('contract.contractCreateSuccess'),
          severity: AlertSeverity.Success,
        });
      } catch (e) {
        showAlert({
          title: t('contract.contractCreateError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
        throw e; // Rethrow to catch later
      }
    },
    [dispatch, ifMounted, showAlert, t, history, companyId]
  );

  const handleSelectContract = useCallback(
    params => {
      history.push(
        AppRoute.ContractDetails.replace(':companyId', params.row.companyId).replace(
          ':contractId',
          params.row.id
        ),
        { from: history.location }
      );
    },
    [history]
  );

  const handleGoBack = useCallback(() => {
    history.push(_.defaultTo(history.location.state?.from, AppRoute.Companies));
  }, [history]);

  return (
    <Box id="CompanyDetails-container" className={classes.root}>
      <LoadingWrapper loading={loading}>
        <Panel
          id="CompanyDetails-panel"
          title={t('company.companyDetails')}
          className={clsx(classes.panel, {
            [classes.fullHeight]: tabIndex !== TabIndex.Details,
          })}
        >
          {!companyDetails ? (
            <EmptyState variant="unavailable" message={t('company.companyDetailsNotAvailable')} />
          ) : (
            <>
              <Tabs
                id="CompanyDetails-tabs"
                className={classes.tabs}
                data-value={tabIndex}
                value={tabIndex}
                indicatorColor="primary"
                textColor="primary"
                onChange={(_event, index) => setTabIndex(index)}
              >
                <Tab
                  id="CompanyDetails-detailsTab"
                  label={t('company.company')}
                  data-index={TabIndex.Details}
                />
                <Tab
                  id="CompanyDetails-contractsTab"
                  label={t('company.companyContracts')}
                  data-index={TabIndex.Contracts}
                />
              </Tabs>
              <Box id="CompanyDetails-tabsContent" className={classes.fullHeight}>
                <TabPanel index={TabIndex.Details} currentIndex={tabIndex}>
                  <CompanyDetailsTab
                    company={companyDetails}
                    onUpdateCompany={handleUpdateCompany}
                    onGoBack={handleGoBack}
                    actionsDisabled={!_.isEmpty(pendingActions)}
                  />
                </TabPanel>
                <TabPanel index={TabIndex.Contracts} currentIndex={tabIndex}>
                  <CompanyContractsTab
                    company={companyDetails}
                    products={products}
                    showActive={showActiveContracts}
                    onSelectContract={handleSelectContract}
                    onCreateContract={handleCreateContract}
                    onFilterChange={() => setShowActiveContracts(!showActiveContracts)}
                    actionsDisabled={!_.isEmpty(pendingActions)}
                  />
                </TabPanel>
              </Box>
            </>
          )}
        </Panel>
      </LoadingWrapper>
    </Box>
  );
}

export default CompanyDetailsPage;
